import { Box, Grid, Typography } from '@mui/material';
import { getDashboardChartGridProps } from 'charts/catalog/analyticsDashboardLayout';
import { CatalogChartRenderer } from 'charts/components/CatalogChartRenderer';
import { ChartResultStatus } from 'charts/catalog/types';
import React, { ReactElement } from 'react';
import { MemoChartReloader } from '../../chartReloader';
import { SectionChartCard } from '../SectionChartCard';
import {
  domainHeadingSx,
  inspectionsDomainSectionSx,
  sectionSx,
} from '../styles';
import type { AnalyticsSectionInspectionsPresentationalProps } from '../types';

export function AnalyticsSectionInspectionsPresentational({
  sectionLabel,
  overviewCharts,
  runtimeById,
  hasSummarySubsection,
  summarySubsection,
  resolveChartTitle,
  resolveChartSubtitle,
  isChartId,
  filters,
}: AnalyticsSectionInspectionsPresentationalProps): ReactElement | null {
  if (overviewCharts.length === 0 && !hasSummarySubsection) {
    return null;
  }

  return (
    <Box component="section" sx={inspectionsDomainSectionSx}>
      <Typography component="h2" sx={domainHeadingSx}>
        {sectionLabel}
      </Typography>

      {filters}

      {overviewCharts.length > 0 ? (
        <Grid container spacing={3} sx={sectionSx}>
          {overviewCharts.map((chart) => {
            if (!isChartId(chart.id)) {
              return null;
            }

            const chartRuntime = runtimeById[chart.id];
            if (!chartRuntime) {
              return null;
            }

            const subtitle = resolveChartSubtitle(chart.id);
            const gridProps = getDashboardChartGridProps(chart.id);

            return (
              <MemoChartReloader key={chart.id} context={chartRuntime}>
                <Grid item {...gridProps}>
                  <SectionChartCard
                    chartId={chart.id}
                    loading={chart.status === ChartResultStatus.loading}
                    title={resolveChartTitle(chartRuntime.titleId)}
                    subtitle={subtitle}
                  >
                    <CatalogChartRenderer chartId={chart.id} />
                  </SectionChartCard>
                </Grid>
              </MemoChartReloader>
            );
          })}
        </Grid>
      ) : null}

      {hasSummarySubsection ? summarySubsection : null}
    </Box>
  );
}

export const MemoAnalyticsSectionInspectionsPresentational = React.memo(
  AnalyticsSectionInspectionsPresentational
);
